import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import toast from 'react-hot-toast';
import './MilestoneCelebration.jsx.css';

const MilestoneCelebration = ({ milestone, onClose, show }) => {
  const [visible, setVisible] = useState(show);

  useEffect(() => {
    setVisible(show);
    if (show) {
      triggerConfetti();
      playSuccessSound();
    }
  }, [show]);

  const triggerConfetti = () => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);
  };

  const playSuccessSound = () => {
    // Optional: Add success sound effect
    try {
      const audio = new Audio('/sounds/success.mp3');
      audio.volume = 0.5;
      audio.play().catch(() => {});
    } catch (e) {}
  };

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  if (!milestone) return null;

  return (
    <AnimatePresence>
      {visible && (
        <>
          <motion.div
            className="celebration-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />
          <motion.div
            className="celebration-modal"
            initial={{ scale: 0.5, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.5, opacity: 0, y: 50 }}
            transition={{ type: 'spring', duration: 0.5 }}
          >
            <motion.div
              className="celebration-icon"
              animate={{
                rotate: [0, -10, 10, -10, 0],
                scale: [1, 1.1, 1, 1.1, 1],
              }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              🎉
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Milestone Achieved!
            </motion.h2>

            <motion.p
              className="celebration-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              {milestone.name || milestone.title}
            </motion.p>

            {milestone.description && (
              <motion.p
                className="celebration-description"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                {milestone.description}
              </motion.p>
            )}

            <motion.div
              className="celebration-stats"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              {milestone.skillsCompleted && (
                <div className="stat">
                  <div className="stat-value">{milestone.skillsCompleted}</div>
                  <div className="stat-label">Skills Mastered</div>
                </div>
              )}
              {milestone.hoursCompleted && (
                <div className="stat">
                  <div className="stat-value">{milestone.hoursCompleted}h</div>
                  <div className="stat-label">Time Invested</div>
                </div>
              )}
              {milestone.progress && (
                <div className="stat">
                  <div className="stat-value">{milestone.progress}%</div>
                  <div className="stat-label">Total Progress</div>
                </div>
              )}
            </motion.div>

            {milestone.badge && (
              <motion.div
                className="celebration-badge"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.7, type: 'spring' }}
              >
                <div className="badge-icon">{milestone.badge.icon || '🏆'}</div>
                <div className="badge-name">{milestone.badge.name}</div>
              </motion.div>
            )}

            <motion.div
              className="celebration-actions"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <button className="btn-secondary" onClick={handleClose}>
                Continue Learning
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  // Share achievement (future implementation)
                  toast.success('Achievement shared!');
                  handleClose();
                }}
              >
                Share Achievement
              </button>
            </motion.div>

            <button className="close-btn" onClick={handleClose}>
              ✕
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export const celebrateMilestone = (milestone) => {
  toast.custom(
    (t) => (
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: t.visible ? 1 : 0, y: t.visible ? 0 : 50 }}
        className="toast-celebration"
      >
        <div className="toast-icon">🎉</div>
        <div className="toast-content">
          <h4>Milestone Unlocked!</h4>
          <p>{milestone.name}</p>
        </div>
      </motion.div>
    ),
    {
      duration: 4000,
      position: 'top-center',
    }
  );
};

export default MilestoneCelebration;
